import React, { useEffect, useRef } from 'react';

/**
 * BOND Animated Logo - canvas particle network + SVG overlay.
 * Rendered inline (no iframe) to avoid proxy redirects intercepting HTML routes.
 *
 * Variants:
 *   - "v1" : Pure gold (no ruby)
 *   - "v3" : Gold + ruby red gem in the O letter (default, "mix")
 *   - "v2" : Original red on dark navy (not implemented here — kept as legacy iframe)
 *
 * The wordmark PNG is pre-tinted to gold (BOND_wordmark_gold.png) so it works
 * in every browser without needing SVG feColorMatrix filters.
 */
export default function BondAnimatedLogo({ variant = 'v3', className = '', style = {} }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const Z = { x0: 67, x1: 930, y0: 86, y1: 339 };
    const N = 70;
    const MD = 190;

    const nodes = Array.from({ length: N }, (_, i) => ({
      x: Z.x0 + 12 + Math.random() * (Z.x1 - Z.x0 - 24),
      y: Z.y0 + 6 + Math.random() * (Z.y1 - Z.y0 - 12),
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      t: i % 9 === 0 ? 2 : i % 4 === 0 ? 1 : 0,
    }));

    let rafId;
    const frame = () => {
      ctx.clearRect(0, 0, 1320, 440);

      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < MD) {
            const a = Math.pow(1 - d / MD, 0.55) * 0.42;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(212,175,55,${a})`;
            ctx.lineWidth = 0.9;
            ctx.stroke();
          }
        }
      }

      for (const n of nodes) {
        if (n.t === 2) {
          [[12, 0.10], [8, 0.20], [5, 0.36]].forEach(([r, a]) => {
            ctx.beginPath();
            ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(245,232,200,${a})`;
            ctx.fill();
          });
          ctx.beginPath();
          ctx.arc(n.x, n.y, 3.5, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255,240,180,1)';
          ctx.fill();
        } else if (n.t === 1) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, 4.5, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(212,175,55,.28)';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(n.x, n.y, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(245,232,200,.95)';
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(n.x, n.y, 1.7, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(212,175,55,.72)';
          ctx.fill();
        }
      }

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < Z.x0 + 8 || n.x > Z.x1 - 8) n.vx *= -1;
        if (n.y < Z.y0 + 4 || n.y > Z.y1 - 4) n.vy *= -1;
        n.x = Math.max(Z.x0 + 8, Math.min(Z.x1 - 8, n.x));
        n.y = Math.max(Z.y0 + 4, Math.min(Z.y1 - 4, n.y));
      }
      rafId = requestAnimationFrame(frame);
    };

    frame();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const showRuby = variant === 'v3';

  return (
    <div
      className={`relative overflow-hidden bg-[#050505] ${className}`}
      style={{ aspectRatio: '1320 / 440', ...style }}
      data-testid="bond-animated-logo"
    >
      <canvas
        ref={canvasRef}
        width={1320}
        height={440}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      />
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 2 }}
        viewBox="0 0 1320 440"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="bond-ruby-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* BOND wordmark — pre-tinted gold PNG, no filter needed */}
        <image
          href="/assets/BOND_wordmark_gold.png"
          x="0"
          y="0"
          width="1320"
          height="440"
        />

        {/* Mirino (crosshair, centered on the O of BOND) */}
        <g stroke="#D4AF37" strokeWidth="3" strokeLinecap="butt" fill="none">
          <line x1="403" y1="192" x2="403" y2="155" />
          <line x1="403" y1="232" x2="403" y2="269" />
          <line x1="383" y1="212" x2="346" y2="212" />
          <line x1="423" y1="212" x2="460" y2="212" />
          <line x1="368" y1="178" x2="357" y2="178" />
          <line x1="368" y1="178" x2="368" y2="189" />
          <line x1="438" y1="178" x2="449" y2="178" />
          <line x1="438" y1="178" x2="438" y2="189" />
          <line x1="368" y1="246" x2="357" y2="246" />
          <line x1="368" y1="246" x2="368" y2="235" />
          <line x1="438" y1="246" x2="449" y2="246" />
          <line x1="438" y1="246" x2="438" y2="235" />
        </g>

        {/* Center dot or Ruby gem */}
        {showRuby ? (
          <g filter="url(#bond-ruby-glow)">
            <ellipse cx="403" cy="214" rx="9" ry="12" fill="#7a0014" />
            <ellipse cx="403" cy="212" rx="6.5" ry="9" fill="#c41030" />
            <ellipse cx="401" cy="208" rx="3" ry="4.5" fill="#ff5066" />
            <circle cx="400" cy="206" r="1.5" fill="#ffe2e8" opacity="0.95" />
          </g>
        ) : (
          <circle cx="403" cy="212" r="4" fill="#D4AF37" />
        )}

        {/* Vertical gold divider */}
        <rect x="990" y="86" width="4" height="253" fill="#D4AF37" />

        {/* Right side text — pure white with / separators */}
        <g
          fontFamily="'Courier New','SF Mono',Menlo,monospace"
          fontSize="22"
          fill="#FFFFFF"
          fontWeight="500"
          letterSpacing="0.5"
        >
          <text x="1008" y="145">BUSINESS /</text>
          <text x="1008" y="180">OPERATIONS /</text>
          <text x="1008" y="215">INTELLIGENCE /</text>
          <text x="1008" y="250">NETWORK /</text>
          <text x="1008" y="285">&amp; DATA</text>
        </g>
      </svg>
    </div>
  );
}
